import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiMapPin, FiUsers, FiTrendingUp } = FiIcons;

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const features = [
    {
      icon: FiAward,
      title: 'Heritage Since 1980',
      description: 'Built on the foundation of Niluka Safari Services, bringing four decades of wildlife expertise to your stay.'
    },
    {
      icon: FiMapPin,
      title: 'Prime Location',
      description: 'Directly opposite the Udawalawe Elephant Transit Home, offering unmatched access to wildlife experiences.'
    },
    {
      icon: FiUsers,
      title: 'Expert Guides',
      description: 'Our staff are not just hospitality professionals—they are safari experts who enhance your wildlife journey.'
    },
    {
      icon: FiTrendingUp,
      title: 'Proven Excellence',
      description: 'Consistently rated 9.4/10 by guests for our exceptional service, facilities, and authentic safari experiences.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            The Heart of <span className="gradient-text">Udawalawe</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter">
            Since 2010, Hotel Athgira has been the trusted basecamp for wildlife enthusiasts. 
            We're not just a place to sleep—we're your gateway to authentic safari experiences.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="glass-effect p-8 rounded-3xl">
              <h3 className="text-3xl font-playfair font-bold text-gray-900 mb-6">
                More Than Just a Hotel
              </h3>
              <p className="text-gray-600 mb-6 font-inter leading-relaxed">
                Hotel Athgira represents the evolution of safari hospitality in Udawalawe. 
                Born from the passion and expertise of Niluka Safari Services, we understand 
                that your accommodation is the foundation of your wildlife adventure.
              </p>
              <p className="text-gray-600 mb-8 font-inter leading-relaxed">
                We've created a space where comfort meets authenticity—where you can rest 
                in air-conditioned comfort after a dusty safari, cool off in our pool, 
                and plan tomorrow's adventure with guides who've been navigating these 
                lands for decades.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span className="text-gray-700 font-inter">Founded in 2010 with deep safari heritage</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span className="text-gray-700 font-inter">Strategic location opposite Elephant Transit Home</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span className="text-gray-700 font-inter">Real rooms, real hospitality, real safari expertise</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop"
                alt="Hotel Athgira exterior"
                className="w-full h-[500px] object-cover hover-lift"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
            
            {/* Floating Stats Card */}
            <motion.div
              className="absolute -bottom-8 -left-8 glass-effect p-6 rounded-2xl"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <div className="text-3xl font-bold text-orange-600 mb-1">15+</div>
              <div className="text-gray-700 font-inter text-sm">Years of Safari Excellence</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              className="glass-effect p-6 rounded-2xl text-center hover-lift group"
            >
              <div className="w-16 h-16 liquid-gradient rounded-full flex items-center justify-center mx-auto mb-4 morphing-blob group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-playfair font-bold text-gray-900 mb-3">
                {feature.title}
              </h4>
              <p className="text-gray-600 font-inter text-sm leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;